import { useEffect } from 'react';
import { usePlaybackStore } from '../stores/playbackStore';
import { useSceneStore } from '../stores/sceneStore';
import { useToolStore, type ToolMode } from '../stores/toolStore';
import { useTimelineStore } from '../stores/timelineStore';
import { useOnionStore } from '../stores/onionStore';
import { useShortcutsStore } from '../stores/shortcutsStore';
import { useCameraBookmarksStore } from '../stores/cameraBookmarksStore';
import { getSpineManager } from '../lib/spine-manager';
import { useDrawingStore } from '../stores/drawingStore';
import { useReviewStore } from '../stores/reviewStore';

const NUDGE_KEYS = new Set(['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown']);

export function useKeyboardShortcuts(
  onFitView?: () => void,
  onToggleShortcuts?: () => void,
) {
  const togglePlay = usePlaybackStore((s) => s.togglePlay);
  const stepForward = usePlaybackStore((s) => s.stepForward);
  const stepBackward = usePlaybackStore((s) => s.stepBackward);
  const stepBy = usePlaybackStore((s) => s.stepBy);
  const { removeLayer, duplicateLayer, setLayerOpacity, selectedLayerId, selectedLayerIds, selectedLayer, selectLayer, undo, redo, save, moveLayerUp, moveLayerDown, pushUndo } = useSceneStore();
  const setTool = useToolStore((s) => s.setTool);
  const cycleBoneMode = useToolStore((s) => s.cycleBoneMode);
  const activeTool = useToolStore((s) => s.activeTool);
  const toggleTimeline = useTimelineStore((s) => s.toggle);
  const toggleOnion = useOnionStore((s) => s.toggle);
  const { togglePresentationMode, exitPresentationMode } = useToolStore();
  const { getActionForKey } = useShortcutsStore();
  const { saveBookmark, getBookmark } = useCameraBookmarksStore();

  useEffect(() => {
    function handler(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement)?.tagName;
      const isInput = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';

      // ── Escape — exit review draw mode first, then deselect / exit presentation ──
      if (e.code === 'Escape') {
        const reviewState = useReviewStore.getState();
        if (reviewState.drawingVersion != null) {
          reviewState.stopReviewDraw();
          return;
        }
        if (activeTool === 'select' && (selectedLayerId || selectedLayerIds.length > 0)) {
          selectLayer(null);
          return;
        }
        exitPresentationMode();
        return;
      }

      // ── Delete / Backspace — remove selected layer(s) (not in inputs) ──
      if ((e.code === 'Delete' || e.code === 'Backspace') && !isInput) {
        const ids = selectedLayerIds.length > 1 ? selectedLayerIds : selectedLayerId ? [selectedLayerId] : [];
        if (ids.length > 0) {
          e.preventDefault();
          pushUndo('Delete layer');
          ids.forEach((id) => removeLayer(id));
        }
        return;
      }

      // ── R — toggle Review panel (not in inputs) ──
      if (e.key === 'r' && !isInput && !e.ctrlKey && !e.metaKey && !e.altKey) {
        useReviewStore.getState().toggle();
        return;
      }

      // ── Ctrl+D — duplicate selected layer(s) ──
      if (e.key === 'd' && (e.ctrlKey || e.metaKey) && !e.shiftKey) {
        e.preventDefault();
        if (selectedLayerIds.length > 1) {
          pushUndo('Duplicate layers');
          selectedLayerIds.forEach((id) => duplicateLayer(id));
        } else if (selectedLayerId) {
          duplicateLayer(selectedLayerId);
        }
        return;
      }

      // ── Ctrl+[ / Ctrl+] — move selected layer up/down in z-order ──
      if ((e.key === '[' || e.key === ']') && !isInput && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        if (selectedLayerId) {
          if (e.key === '[') moveLayerUp(selectedLayerId);
          else moveLayerDown(selectedLayerId);
        }
        return;
      }

      // ── [ / ] — decrease/increase selected layer opacity by 10% ──
      if ((e.key === '[' || e.key === ']') && !isInput && !e.ctrlKey && !e.metaKey) {
        const layer = selectedLayer();
        if (layer && selectedLayerId) {
          e.preventDefault();
          const delta = e.key === '[' ? -0.1 : 0.1;
          const next = Math.max(0, Math.min(1, layer.opacity + delta));
          setLayerOpacity(selectedLayerId, Math.round(next * 10) / 10);
          getSpineManager().setLayerOpacity(selectedLayerId, Math.round(next * 10) / 10);
        }
        return;
      }

      // ── H / V — flip selected layer horizontally / vertically ──
      if ((e.key === 'h' || e.key === 'v') && !isInput && !e.ctrlKey && !e.metaKey && !e.altKey && activeTool === 'select') {
        const layer = selectedLayer();
        if (layer && selectedLayerId) {
          e.preventDefault();
          if (e.key === 'h') {
            useSceneStore.getState().updateLayerTransform(selectedLayerId, { scaleX: -layer.transform.scaleX });
          } else {
            useSceneStore.getState().updateLayerTransform(selectedLayerId, { scaleY: -layer.transform.scaleY });
          }
        }
        return;
      }

      // ── Ctrl+Z — undo (draw strokes first when in draw mode) ──
      if (e.key === 'z' && (e.ctrlKey || e.metaKey) && !e.shiftKey) {
        e.preventDefault();
        if (activeTool === 'draw') {
          const { sceneId } = useSceneStore.getState();
          const { currentFrame } = usePlaybackStore.getState();
          const { drawings, undoLastStroke: drawUndo } = useDrawingStore.getState();
          if (sceneId && (drawings[currentFrame]?.length ?? 0) > 0) {
            drawUndo(sceneId, currentFrame);
            return;
          }
        }
        undo();
        return;
      }

      // ── Ctrl+Y or Ctrl+Shift+Z — redo (draw strokes first when in draw mode) ──
      if (
        (e.key === 'y' && (e.ctrlKey || e.metaKey)) ||
        (e.key === 'z' && (e.ctrlKey || e.metaKey) && e.shiftKey)
      ) {
        e.preventDefault();
        if (activeTool === 'draw') {
          const { sceneId } = useSceneStore.getState();
          const { currentFrame } = usePlaybackStore.getState();
          const { redoStacks, redoLastStroke: drawRedo } = useDrawingStore.getState();
          if (sceneId && (redoStacks[currentFrame]?.length ?? 0) > 0) {
            drawRedo(sceneId, currentFrame);
            return;
          }
        }
        redo();
        return;
      }

      // ── Ctrl+S — save ──
      if (e.key === 's' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        save();
        return;
      }

      // ── Camera bookmarks: Ctrl+1-9 = save, Alt+1-9 = recall ──
      if (!isInput && (e.ctrlKey || e.metaKey) && !e.shiftKey && e.key >= '1' && e.key <= '9') {
        e.preventDefault();
        const vp = getSpineManager().getViewportState();
        saveBookmark(e.key, { zoom: vp.zoom, x: vp.x, y: vp.y });
        return;
      }
      if (!isInput && e.altKey && e.key >= '1' && e.key <= '9') {
        e.preventDefault();
        const bm = getBookmark(e.key);
        if (bm) getSpineManager().setViewport(bm.x, bm.y, bm.zoom);
        return;
      }

      // ── Arrow keys — nudge selected layer OR step animation ──
      // Skip if focus is inside a nav panel (hierarchy/assets) — those handle their own navigation
      const isInNavPanel = !!(document.activeElement?.closest('[data-nav-panel="hierarchy"]'));
      if (NUDGE_KEYS.has(e.code) && !isInput && !isInNavPanel) {
        const { isPlaying } = usePlaybackStore.getState();
        const currentLayer = selectedLayer();
        const activeTool = useToolStore.getState().activeTool;
        // Nudge layer only while playing — when paused, left/right always step frames for frame review
        if (isPlaying && currentLayer && selectedLayerId && (activeTool === 'select' || activeTool === 'move')) {
          e.preventDefault();
          const amount = e.shiftKey ? 10 : 1;
          const dx = e.code === 'ArrowRight' ? amount : e.code === 'ArrowLeft' ? -amount : 0;
          const dy = e.code === 'ArrowDown' ? amount : e.code === 'ArrowUp' ? -amount : 0;
          useSceneStore.getState().updateLayerTransform(selectedLayerId, {
            x: currentLayer.transform.x + dx,
            y: currentLayer.transform.y + dy,
          });
          return;
        }
        if (e.code === 'ArrowRight') { e.preventDefault(); e.shiftKey ? stepBy(10) : stepForward(); }
        else if (e.code === 'ArrowLeft') { e.preventDefault(); e.shiftKey ? stepBy(-10) : stepBackward(); }
        return;
      }

      // ── Remappable single-key shortcuts (no modifier, not in input) ──
      if (!isInput && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const key = e.key === ' ' ? ' ' : e.key.length === 1 ? e.key.toLowerCase() : e.code;
        const action = getActionForKey(key === ' ' ? ' ' : e.key.toLowerCase()) ?? getActionForKey(e.code);

        if (action) {
          e.preventDefault();
          switch (action) {
            case 'play': togglePlay(); break;
            case 'toolSelect': setTool('select' as ToolMode); break;
            case 'toolMove':   setTool('move'   as ToolMode); break;
            case 'toolRotate': setTool('rotate' as ToolMode); break;
            case 'toolScale':  setTool('scale'  as ToolMode); break;
            case 'toolPan':    setTool('pan'    as ToolMode); break;
            case 'toggleBones': cycleBoneMode(); break;
            case 'toggleTimeline': toggleTimeline(); break;
            case 'toggleOnion':    toggleOnion(); break;
            case 'fitView': onFitView?.(); break;
            case 'togglePresentation': togglePresentationMode(); break;
            case 'zoomIn':  getSpineManager().zoomIn(); break;
            case 'zoomOut': getSpineManager().zoomOut(); break;
          }
          return;
        }

        // Non-remappable extras
        if (e.key === '?') {
          e.preventDefault();
          onToggleShortcuts?.();
          return;
        }

        // I / Shift+I — set loop in / out point
        if (e.key.toLowerCase() === 'i') {
          e.preventDefault();
          const { currentFrame, setLoopStart, setLoopEnd } = usePlaybackStore.getState();
          if (e.shiftKey) setLoopEnd(currentFrame);
          else setLoopStart(currentFrame);
          return;
        }

        // J / K / L — video-style speed control
        const SPEED_PRESETS = [0.25, 0.5, 1, 2, 4];
        if (e.key === 'j' || e.key === 'k' || e.key === 'l') {
          e.preventDefault();
          const { speed, setSpeed, play, pause } = usePlaybackStore.getState();
          if (e.key === 'k') { pause(); return; }
          const idx = SPEED_PRESETS.indexOf(speed);
          const safeIdx = idx === -1 ? 2 : idx; // fall back to 1x if custom speed
          if (e.key === 'j') setSpeed(SPEED_PRESETS[Math.max(0, safeIdx - 1)]);
          if (e.key === 'l') setSpeed(SPEED_PRESETS[Math.min(SPEED_PRESETS.length - 1, safeIdx + 1)]);
          play();
          return;
        }
        // F5 fallback (remappable action may have F5 as key code)
        if (e.code === 'F5') {
          e.preventDefault();
          togglePresentationMode();
          return;
        }
      }
    }

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [
    togglePlay, removeLayer, duplicateLayer, setLayerOpacity,
    selectedLayerId, selectedLayerIds, selectedLayer, selectLayer,
    undo, redo, save, moveLayerUp, moveLayerDown,
    stepForward, stepBackward, stepBy, setTool, cycleBoneMode,
    toggleTimeline, toggleOnion, onFitView, onToggleShortcuts,
    togglePresentationMode, exitPresentationMode,
    getActionForKey, saveBookmark, getBookmark, pushUndo,
    activeTool,
  ]);

}
